/**
 * FACE RIG — runtime driver for body and authored-head expression blendshapes.
 * The shared ARKit-name subset supports speech, capture and emotions. Influences live on
 * the MESH (morphTargetInfluences), so every avatar's face is independent even though the
 * morph delta buffers are shared templates.
 *
 * Channels, blended each frame (later wins where it speaks):
 *   1. EMOTE preset (or none): eased toward with attack/release smoothing
 *      Authored PERFORMANCE temporarily replaces that target and the random talk nod.
 *   2. VISEME talk channel: text-driven mouth shapes (speak()/feedChar()) — additive on
 *      top of the emote's mouth, replacing it while a syllable is live
 *   3. AUTO-BLINK: periodic lid closes, max()ed over whatever the other channels say
 *   4. CAPTURED frame (Avatar Lab webcam / clip playback): replaces the emote channel.
 *      A live text viseme still temporarily owns only the mouth shapes, so SPEAK can be
 *      auditioned while webcam head, gaze, eyes, and brows remain active.
 *
 * The rig never touches bones — head nods ride `headOffset` (radians), which the avatar
 * composes onto the head bone AFTER its own head-look solves (aim still wins).
 */

export type FaceFrame = Record<string, number>;

/** Emote presets — combos over the baked shape set. Also the vocabulary for dialog
 *  emotion tags and the future emote wheel. */
export const FACE_EMOTES: Record<string, FaceFrame> = {
  neutral: {},
  smile: { mouthSmileLeft: 0.85, mouthSmileRight: 0.85, eyeSquintLeft: 0.55, eyeSquintRight: 0.55 },
  grin: { mouthSmileLeft: 1, mouthSmileRight: 1, jawOpen: 0.22, browOuterUpLeft: 0.25, browOuterUpRight: 0.25 },
  laugh: { mouthSmileLeft: 1, mouthSmileRight: 1, jawOpen: 0.5, eyeBlinkLeft: 0.35, eyeBlinkRight: 0.35, browOuterUpLeft: 0.4, browOuterUpRight: 0.4 },
  smirk: { mouthSmileLeft: 0.9, browOuterUpLeft: 0.3 },
  sad: { mouthFrownLeft: 0.8, mouthFrownRight: 0.8, browInnerUp: 0.9, eyeSadLeft: 0.95, eyeSadRight: 0.95 },
  angry: { browDownLeft: 1, browDownRight: 1, mouthFrownLeft: 0.5, mouthFrownRight: 0.5, mouthStretchLeft: 0.3, mouthStretchRight: 0.3, eyeTenseLeft: 0.85, eyeTenseRight: 0.85, eyeSquintLeft: 0.15, eyeSquintRight: 0.15 },
  shocked: { browInnerUp: 1, browOuterUpLeft: 1, browOuterUpRight: 1, eyeWideLeft: 1, eyeWideRight: 1, jawOpen: 0.45 },
  suspicious: { browDownLeft: 0.85, eyeBlinkLeft: 0.25, eyeBlinkRight: 0.25, mouthPucker: 0.2 },
  wink: { eyeBlinkLeft: 1, mouthSmileLeft: 0.45 },
  puff: { cheekPuff: 1 },
  // Calibrated against the supplied female expression sheet. Keep the existing
  // dialog vocabulary above; these names are also available to emote callers.
  happy: { mouthSmileLeft: 0.72, mouthSmileRight: 0.72, browOuterUpLeft: 0.12, browOuterUpRight: 0.12, eyeSquintLeft: 0.7, eyeSquintRight: 0.7 },
  confused: { browInnerUp: 0.32, browDownLeft: 0.48, browOuterUpRight: 0.52, mouthFrownLeft: 0.30, mouthPucker: 0.16, eyeTenseLeft: 0.55, eyeSquintLeft: 0.2, eyeWideRight: 0.3 },
  surprised: { browInnerUp: 0.72, browOuterUpLeft: 0.65, browOuterUpRight: 0.65, eyeWideLeft: 1, eyeWideRight: 1, jawOpen: 0.30, mouthFunnel: 0.22 },
  disgusted: { browDownLeft: 0.65, browDownRight: 0.65, noseSneerLeft: 0.9, noseSneerRight: 0.65, mouthFrownLeft: 0.45, mouthFrownRight: 0.45, mouthPress: 0.15, eyeSquintLeft: 0.65, eyeSquintRight: 0.65, eyeTenseLeft: 0.35, eyeTenseRight: 0.35 },
  fearful: { browInnerUp: 1, browOuterUpLeft: 0.35, browOuterUpRight: 0.35, eyeWideLeft: 0.85, eyeWideRight: 0.85, eyeSquintLeft: 0.15, eyeSquintRight: 0.15, jawOpen: 0.24, mouthStretchLeft: 0.25, mouthStretchRight: 0.25, mouthFrownLeft: 0.25, mouthFrownRight: 0.25 },
  determined: { browDownLeft: 0.45, browDownRight: 0.45, mouthPress: 0.75, eyeTenseLeft: 0.7, eyeTenseRight: 0.7, eyeSquintLeft: 0.2, eyeSquintRight: 0.2 },
  // SLEEP (2026-07-31 play report: sleeping residents slept with their eyes
  // open). Lids fully shut, brow and mouth soft. The auto-blink channel takes
  // max(target, blink) on the lids, so a blink can never re-open a closed eye —
  // the preset needs no blink suspension to hold.
  sleep: { eyeBlinkLeft: 1, eyeBlinkRight: 1, browInnerUp: 0.1, mouthPucker: 0.08 },
};

/** Mouth shapes per viseme class (the text-driven talk vocabulary). */
const VISEMES: Record<string, FaceFrame> = {
  A: { jawOpen: 0.42 },
  E: { jawOpen: 0.18, mouthStretchLeft: 0.6, mouthStretchRight: 0.6 },
  I: { jawOpen: 0.24, mouthStretchLeft: 0.35, mouthStretchRight: 0.35 },
  O: { jawOpen: 0.35, mouthFunnel: 0.8 },
  U: { jawOpen: 0.12, mouthPucker: 0.85 },
  M: { mouthPucker: 0.15 }, // lips together
  F: { jawOpen: 0.08, mouthFrownLeft: 0.2, mouthFrownRight: 0.2 },
  C: { jawOpen: 0.16, mouthStretchLeft: 0.15, mouthStretchRight: 0.15 }, // generic consonant
  rest: {},
};

const CHAR_VISEME: Record<string, string> = {
  a: "A", e: "E", i: "I", o: "O", u: "U", y: "I",
  m: "M", b: "M", p: "M",
  f: "F", v: "F",
  w: "U", q: "U",
};

/** Every shape any channel can write — the union we reset each frame. The first
 *  nineteen match the legacy FACE_MORPH_NAMES; newer authored heads can supply
 *  additional optional poses without changing older assets. */
export const FACE_SHAPES = [
  "jawOpen", "mouthSmileLeft", "mouthSmileRight", "mouthFrownLeft", "mouthFrownRight",
  "mouthPucker", "mouthFunnel", "mouthStretchLeft", "mouthStretchRight", "browInnerUp",
  "browDownLeft", "browDownRight", "browOuterUpLeft", "browOuterUpRight",
  "eyeBlinkLeft", "eyeBlinkRight", "eyeWideLeft", "eyeWideRight", "cheekPuff",
  // Optional authored female poses; older/male assets safely omit these keys.
  "noseSneerLeft", "noseSneerRight", "mouthPress",
  "eyeSquintLeft", "eyeSquintRight", "eyeSadLeft", "eyeSadRight", "eyeTenseLeft", "eyeTenseRight",
] as const;
const ALL_SHAPES = FACE_SHAPES as readonly string[];
const LIDS_LEFT = ["eyeWideLeft", "eyeSquintLeft", "eyeSadLeft", "eyeTenseLeft"];
const LIDS_RIGHT = ["eyeWideRight", "eyeSquintRight", "eyeSadRight", "eyeTenseRight"];
const LID_SIDE: Record<string, "left" | "right"> = Object.fromEntries([
  ...LIDS_LEFT.map(n => [n, "left"] as const), ...LIDS_RIGHT.map(n => [n, "right"] as const),
]);

export class FaceRig {
  /** Head-local offset (radians: pitch +down-nod, yaw, roll) for the avatar to compose
   *  after its head-look solves. Captured channel + a subtle talk nod write it. */
  readonly headOffset = { pitch: 0, yaw: 0, roll: 0 };

  private current = new Map<string, number>(ALL_SHAPES.map((n) => [n, 0]));
  private emote: FaceFrame = {};
  private emoteUntil = Infinity; // performance-clock seconds; Infinity = hold until cleared
  private captured: FaceFrame | null = null;
  private capturedHead: { pitch: number; yaw: number; roll: number } | null = null;
  private performance: { face: FaceFrame; pitch: number } | null = null;
  /** Captured eye GAZE (radians, +yaw = character's right, +pitch = up) — when set, the
   *  avatar's eye-look layer follows it instead of idle saccades. */
  capturedGaze: { yaw: number; pitch: number } | null = null;

  // talk channel
  private viseme: FaceFrame = {};
  private visemeT = 0;      // seconds the current viseme stays live before decaying
  private speakText = "";   // self-paced speech (in-world flap w/o a UI typewriter)
  private speakI = 0;
  private speakT = 0;
  private speakCps = 16;
  private talkPhase = Math.random() * 10; // head-nod phase (desynced between avatars)

  // auto-blink
  private blinkT = 1.5 + Math.random() * 3;
  private blinkPhase = 0; // >0 while a blink is running (seconds into it)

  private clock = 0;

  /** Hold an emote preset (name from FACE_EMOTES) or a raw frame. holdSec: auto-clear. */
  setEmote(emote: string | FaceFrame | null, holdSec?: number): void {
    this.emote = emote == null ? {} : typeof emote === "string" ? (FACE_EMOTES[emote] ?? {}) : emote;
    this.emoteUntil = holdSec !== undefined ? this.clock + holdSec : Infinity;
  }

  /** Authored conversation channel. Capture still wins; speech and blinking remain live.
   * Clearing it restores the underlying emote instead of destroying ambient state. */
  setPerformance(frame: { face: FaceFrame; pitch: number } | null): void {
    this.performance = frame;
  }

  /** Live capture / clip playback: replaces the emote channel until cleared. Text visemes
   *  may still override only the mouth subset while a sentence is speaking. */
  setCaptured(
    frame: FaceFrame | null,
    head?: { pitch: number; yaw: number; roll: number } | null,
    gaze?: { yaw: number; pitch: number } | null,
  ): void {
    this.captured = frame;
    this.capturedHead = head ?? null;
    this.capturedGaze = frame ? (gaze ?? null) : null;
  }

  /** Feed one typed character (dialog typewriter drives the on-screen NPC's mouth). */
  feedChar(c: string): void {
    const v = CHAR_VISEME[c.toLowerCase()] ?? (/[a-z]/i.test(c) ? "C" : "rest");
    this.viseme = VISEMES[v];
    this.visemeT = 0.14;
  }

  /** Self-paced talk flap for `text` at `cps` chars/sec (no UI typewriter needed). */
  speak(text: string, cps = 16): void {
    this.speakText = text;
    this.speakI = 0;
    this.speakT = 0;
    this.speakCps = Math.max(4, cps);
  }

  stopSpeaking(): void {
    this.speakText = "";
    this.viseme = {};
    this.visemeT = 0;
  }

  get speaking(): boolean {
    return this.speakI < this.speakText.length || this.visemeT > 0;
  }

  /** Advance channels and write influences via `write` (name, value). Call once per frame. */
  update(dt: number, write: (name: string, v: number) => void): void {
    this.clock += dt;

    // self-paced speech feeds the viseme channel
    if (this.speakI < this.speakText.length) {
      this.speakT -= dt;
      while (this.speakT <= 0 && this.speakI < this.speakText.length) {
        this.feedChar(this.speakText[this.speakI++]);
        this.speakT += 1 / this.speakCps;
      }
    }
    if (this.visemeT > 0) this.visemeT -= dt;

    // auto-blink timer/curve
    let blink = 0;
    if (this.blinkPhase > 0) {
      const t = (this.blinkPhase += dt);
      blink = t < 0.09 ? t / 0.09 : t < 0.13 ? 1 : t < 0.27 ? 1 - (t - 0.13) / 0.14 : 0;
      if (t >= 0.27) {
        this.blinkPhase = 0;
        this.blinkT = 2.2 + Math.random() * 4;
      }
    } else if ((this.blinkT -= dt) <= 0) {
      this.blinkPhase = 1e-6;
    }

    // target vector
    const target = this.captured ?? this.performance?.face ?? this.emote;
    if (!this.captured && this.clock > this.emoteUntil) this.emote = {};
    const talking = this.visemeT > 0;

    // Authored accent replaces the automatic talk nod; captured head wins outright.
    if (this.capturedHead) {
      this.headOffset.pitch = this.capturedHead.pitch;
      this.headOffset.yaw = this.capturedHead.yaw;
      this.headOffset.roll = this.capturedHead.roll;
    } else {
      const nod = this.performance ? Math.max(-0.18, Math.min(0.18, this.performance.pitch)) :
        this.speaking ? Math.sin((this.clock + this.talkPhase) * 3.1) * 0.025 : 0;
      const k = 1 - Math.exp(-(this.performance ? 18 : 6) * dt);
      this.headOffset.pitch += (nod - this.headOffset.pitch) * k;
      this.headOffset.yaw *= 1 - k;
      this.headOffset.roll *= 1 - k;
    }

    for (const name of ALL_SHAPES) {
      let t = target[name] ?? 0;
      if (talking && name in this.activeVisemeUnion()) t = this.viseme[name] ?? 0;
      if (LID_SIDE[name]) t = Math.max(0, Math.min(1, t));
      // blink rides on top unless the captured frame drives the lids itself
      if ((name === "eyeBlinkLeft" || name === "eyeBlinkRight") &&
          !(this.captured && name in this.captured)) {
        t = Math.max(t, blink);
      }
      const cur = this.current.get(name)!;
      // captured frames are already source-smoothed -> track fast; else attack/release ease
      const k = this.captured ? 1 - Math.exp(-40 * dt) : t > cur ? 1 - Math.exp(-25 * dt) : 1 - Math.exp(-14 * dt);
      const next = Math.abs(t - cur) < 0.001 ? t : cur + (t - cur) * k;
      if (next !== cur) this.current.set(name, next);
    }
    // Resolve lids AFTER smoothing. Otherwise a decaying emotional pose can
    // remain underneath a closing blink and push the lids through one another.
    // Each eye blends within its available aperture; full blink owns it entirely.
    const left = (1 - Math.max(0, Math.min(1, this.current.get("eyeBlinkLeft")!))) /
      Math.max(1, LIDS_LEFT.reduce((sum, n) => sum + this.current.get(n)!, 0));
    const right = (1 - Math.max(0, Math.min(1, this.current.get("eyeBlinkRight")!))) /
      Math.max(1, LIDS_RIGHT.reduce((sum, n) => sum + this.current.get(n)!, 0));
    for (const name of ALL_SHAPES) write(name, this.current.get(name)! *
      (LID_SIDE[name] === "left" ? left : LID_SIDE[name] === "right" ? right : 1));
  }

  /** Mouth shapes the talk channel owns while a syllable is live (emote mouth yields). */
  private static MOUTH = new Set([
    "jawOpen", "mouthSmileLeft", "mouthSmileRight", "mouthFrownLeft", "mouthFrownRight",
    "mouthPucker", "mouthFunnel", "mouthStretchLeft", "mouthStretchRight",
    "mouthPress",
  ]);
  private mouthUnion: Record<string, 1> | null = null;
  private activeVisemeUnion(): Record<string, 1> {
    if (!this.mouthUnion) {
      this.mouthUnion = {};
      for (const n of FaceRig.MOUTH) this.mouthUnion[n] = 1;
    }
    return this.mouthUnion;
  }
}
